import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { xrayMachineService } from '../../../services/xrayMachineService';

const XrayMachineConnection = ({ onImageAcquired, patientId }) => {
  const [machines, setMachines] = useState([]);
  const [selectedMachine, setSelectedMachine] = useState(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isAcquiring, setIsAcquiring] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const [acquisitionParams, setAcquisitionParams] = useState({
    imageType: 'xray',
    category: 'Periapical',
    toothNumber: '',
    kvp: 70,
    exposure: 8,
    modality: 'DX'
  });
  const [showAdvanced, setShowAdvanced] = useState(false);

  const imageCategories = [
    { value: 'Periapical', label: 'Periapical' },
    { value: 'Bitewing', label: 'Bitewing' },
    { value: 'Panoramic', label: 'Panoramic' },
    { value: 'Cone Beam CT', label: 'Cone Beam CT' },
    { value: 'Cephalometric', label: 'Cephalometric' }
  ];

  useEffect(() => {
    discoverMachines();
  }, []);

  const discoverMachines = async () => {
    const result = await xrayMachineService?.discoverMachines();
    if (result?.success) {
      setMachines(result?.machines || []);
    }
  };

  const handleConnect = async (machine) => {
    setIsConnecting(true);
    const result = await xrayMachineService?.connectToMachine(machine?.id, {
      aeTitle: machine?.aeTitle,
      ipAddress: machine?.ipAddress,
      port: machine?.port
    });

    if (result?.success) {
      setSelectedMachine(machine);
      setConnectionStatus('connected');
    }
    setIsConnecting(false);
  };

  const handleDisconnect = async () => {
    if (!selectedMachine) return;
    
    const result = await xrayMachineService?.disconnectFromMachine(selectedMachine?.id);
    if (result?.success) {
      setSelectedMachine(null);
      setConnectionStatus('disconnected');
    }
  };

  const handleAcquireImage = async () => {
    if (!selectedMachine || !patientId) return;

    setIsAcquiring(true);
    const result = await xrayMachineService?.acquireImage(selectedMachine?.id, {
      ...acquisitionParams,
      patientId,
      providerId: 'current-provider-id',
      manufacturer: selectedMachine?.manufacturer
    });

    if (result?.success) {
      onImageAcquired?.(result?.image);
      // Reset params
      setAcquisitionParams({
        imageType: 'xray',
        category: 'Periapical',
        toothNumber: '',
        kvp: 70,
        exposure: 8,
        modality: 'DX'
      });
    }
    setIsAcquiring(false);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'online':
        return 'text-success';
      case 'offline':
        return 'text-muted-foreground';
      case 'busy':
        return 'text-warning';
      default:
        return 'text-muted-foreground';
    }
  };

  return (
    <div className="bg-card rounded-lg border border-border shadow-elevation-2 h-full flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
            <Icon name="Wifi" size={16} />
            X-ray Machine Connection
          </h3>
          <Button
            variant="ghost"
            size="xs"
            iconName="RefreshCw"
            onClick={discoverMachines}
          >
            Refresh
          </Button>
        </div>
        
        {connectionStatus === 'connected' && selectedMachine && (
          <div className="flex items-center gap-2 text-xs text-success bg-success/10 px-3 py-2 rounded">
            <Icon name="CheckCircle" size={14} />
            <span>Connected to {selectedMachine?.name}</span>
          </div>
        )}
      </div>

      {/* Machine List */}
      {connectionStatus === 'disconnected' && (
        <div className="flex-1 overflow-y-auto p-4">
          <div className="space-y-2">
            {machines?.length === 0 ? (
              <div className="text-center py-8">
                <Icon name="Wifi" size={32} className="mx-auto text-muted-foreground mb-2" />
                <p className="text-sm text-muted-foreground">No X-ray machines found</p>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={discoverMachines}
                  className="mt-2"
                >
                  Scan Network
                </Button>
              </div>
            ) : (
              machines?.map((machine) => (
                <div
                  key={machine?.id}
                  className="p-3 rounded-lg border border-border hover:border-primary/50 hover:bg-muted/50 transition-all"
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <Icon name="Monitor" size={14} className="text-primary" />
                        <span className="text-sm font-medium text-foreground">
                          {machine?.name}
                        </span>
                        <div className={`flex items-center gap-1 text-xs ${getStatusColor(machine?.status)}`}>
                          <div className="w-2 h-2 rounded-full bg-current" />
                          <span className="capitalize">{machine?.status}</span>
                        </div>
                      </div>
                      <div className="text-xs text-muted-foreground space-y-1">
                        <div>{machine?.manufacturer} - {machine?.model}</div>
                        <div className="flex items-center gap-1">
                          <Icon name="Network" size={10} />
                          <span>{machine?.ipAddress}:{machine?.port}</span>
                        </div>
                        <div className="flex items-center gap-1 flex-wrap">
                          {machine?.capabilities?.map((cap) => (
                            <span
                              key={cap}
                              className="px-2 py-0.5 bg-primary/10 text-primary rounded text-xs"
                            >
                              {cap}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                    <Button
                      variant="default"
                      size="xs"
                      onClick={() => handleConnect(machine)}
                      disabled={isConnecting || machine?.status !== 'online'}
                    >
                      {isConnecting ? 'Connecting...' : 'Connect'}
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Acquisition Panel */}
      {connectionStatus === 'connected' && selectedMachine && (
        <div className="flex-1 overflow-y-auto p-4">
          <div className="space-y-4">
            {/* Connected Machine Info */}
            <div className="p-3 rounded-lg bg-muted/30 border border-border">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Icon name="Monitor" size={14} className="text-primary" />
                  <span className="text-sm font-medium text-foreground">
                    {selectedMachine?.name}
                  </span>
                </div>
                <Button
                  variant="ghost"
                  size="xs"
                  onClick={handleDisconnect}
                >
                  Disconnect
                </Button>
              </div>
              <div className="text-xs text-muted-foreground">
                {selectedMachine?.manufacturer} - {selectedMachine?.model}
              </div>
            </div>

            {/* Acquisition Parameters */}
            <div className="space-y-3">
              <h4 className="text-sm font-medium text-foreground">Image Acquisition</h4>
              
              <Select
                label="Image Category"
                value={acquisitionParams?.category}
                onChange={(value) => setAcquisitionParams({ ...acquisitionParams, category: value })}
                options={imageCategories}
              />

              <Input
                label="Tooth Number"
                placeholder="e.g., #14 or #3-5"
                value={acquisitionParams?.toothNumber}
                onChange={(e) => setAcquisitionParams({ ...acquisitionParams, toothNumber: e?.target?.value })}
              />

              {/* Advanced Settings */}
              <div>
                <button
                  className="flex items-center gap-1 text-xs text-primary hover:underline"
                  onClick={() => setShowAdvanced(!showAdvanced)}
                >
                  <Icon name={showAdvanced ? 'ChevronDown' : 'ChevronRight'} size={12} />
                  Advanced Settings
                </button>
                
                {showAdvanced && (
                  <div className="mt-3 space-y-3 pl-4">
                    <Input
                      label="kVp (Kilovoltage)"
                      type="number"
                      value={acquisitionParams?.kvp}
                      onChange={(e) => setAcquisitionParams({ ...acquisitionParams, kvp: parseInt(e?.target?.value) })}
                    />
                    <Input
                      label="Exposure (mAs)"
                      type="number"
                      value={acquisitionParams?.exposure}
                      onChange={(e) => setAcquisitionParams({ ...acquisitionParams, exposure: parseInt(e?.target?.value) })}
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Acquire Button */}
            <Button
              variant="default"
              className="w-full"
              onClick={handleAcquireImage}
              disabled={isAcquiring || !acquisitionParams?.toothNumber}
              iconName="Camera"
            >
              {isAcquiring ? 'Acquiring Image...' : 'Acquire Image'}
            </Button>

            {/* Instructions */}
            <div className="p-3 rounded-lg bg-blue-50 border border-blue-200">
              <div className="flex gap-2">
                <Icon name="Info" size={14} className="text-blue-600 flex-shrink-0 mt-0.5" />
                <div className="text-xs text-blue-900">
                  <p className="font-medium mb-1">Acquisition Instructions:</p>
                  <ol className="list-decimal list-inside space-y-1">
                    <li>Position the patient correctly</li>
                    <li>Select image category and tooth number</li>
                    <li>Adjust exposure settings if needed</li>
                    <li>Click "Acquire Image" to capture</li>
                  </ol>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default XrayMachineConnection;
